<template>
  <section
    v-if="hasContent"
    v-reveal
    :id="anchorId"
    class="v2-research"
  >
    <!-- Background: navy → deep purple → navy base + pink/blue overlay mesh -->
    <div class="v2-research__bg-primary" aria-hidden="true"></div>
    <div class="v2-research__bg-mesh" aria-hidden="true"></div>

    <div class="v2-research__container">
      <div class="v2-research__header">
        <span
          v-if="researchModel.eyebrow"
          class="v2-research__eyebrow"
        >
          {{ researchModel.eyebrow }}
        </span>
        <h2 class="v2-research__title">
          {{ researchModel.headline }}
        </h2>
      </div>

      <!-- Desktop: horizontal row with arrows between steps -->
      <div class="v2-research__steps-row v2-research__steps-row--desktop">
        <template v-for="(step, i) in steps" :key="'d-' + i">
          <div class="v2-research__step">
            <div class="v2-research__step-circle">
              <img
                v-if="stepImage(step)"
                :src="stepImage(step)"
                :alt="step.title"
                class="v2-research__step-img"
              />
              <div v-else class="v2-research__step-fallback">{{ i + 1 }}</div>
            </div>
            <h3 class="v2-research__step-title">{{ step.title }}</h3>
            <p class="v2-research__step-body">{{ step.body }}</p>
          </div>
          <div
            v-if="i < steps.length - 1"
            class="v2-research__arrow v2-research__arrow--right"
            aria-hidden="true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </div>
        </template>
      </div>

      <!-- Mobile: vertical stack with arrows below each (except last) -->
      <div class="v2-research__steps-col v2-research__steps-col--mobile">
        <template v-for="(step, i) in steps" :key="'m-' + i">
          <div class="v2-research__step v2-research__step--mobile">
            <div class="v2-research__step-circle v2-research__step-circle--mobile">
              <img
                v-if="stepImage(step)"
                :src="stepImage(step)"
                :alt="step.title"
                class="v2-research__step-img"
              />
              <div v-else class="v2-research__step-fallback">{{ i + 1 }}</div>
            </div>
            <h3 class="v2-research__step-title">{{ step.title }}</h3>
            <p class="v2-research__step-body">{{ step.body }}</p>
          </div>
          <div
            v-if="i < steps.length - 1"
            class="v2-research__arrow v2-research__arrow--down"
            aria-hidden="true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <polyline points="19 12 12 19 5 12"/>
            </svg>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
// Fallback map: if a step's `icon` field is a known keyword, resolve to
// the matching image in /img/dig-pink-v2. This keeps existing seeded
// content (icon: 'help' / 'data' / ...) working without a schema change.
const ICON_FALLBACK_MAP = {
  help: '/img/dig-pink-v2/step-help.jpg',
  data: '/img/dig-pink-v2/step-data.jpg',
  discovery: '/img/dig-pink-v2/step-research.jpg',
  research: '/img/dig-pink-v2/step-research.jpg',
  share: '/img/dig-pink-v2/step-share.jpg',
}

export default {
  name: 'V2ResearchModel',
  props: {
    researchModel: { type: Object, default: () => ({ steps: [] }) },
    anchorId: { type: String, default: 'impact' },
  },
  computed: {
    steps() {
      return Array.isArray(this.researchModel?.steps) ? this.researchModel.steps : []
    },
    hasContent() {
      return this.researchModel?.headline || this.steps.length > 0
    },
  },
  methods: {
    stepImage(step) {
      // Prefer an explicit image URL
      if (step.image && typeof step.image === 'string') return step.image
      // If the `icon` field is a path-like string, treat as image URL
      const icon = step.icon
      if (typeof icon === 'string') {
        if (icon.startsWith('/') || icon.startsWith('http')) return icon
        // Otherwise look up the keyword fallback
        if (ICON_FALLBACK_MAP[icon.toLowerCase()]) return ICON_FALLBACK_MAP[icon.toLowerCase()]
      }
      return null
    },
  },
}
</script>

<style scoped>
.v2-research {
  position: relative;
  overflow: hidden;
  padding-top: 64px;
  padding-bottom: 64px;
  color: #ffffff;
}
@media (min-width: 1024px) {
  .v2-research { padding-top: 96px; padding-bottom: 96px; }
}

/* Layered background: navy → deep purple → navy. Slow drift via
   background-position shift keeps the purple midtone subtly alive
   without distracting from the content. 25s cycle, ease-in-out. */
.v2-research__bg-primary {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: linear-gradient(
    135deg,
    var(--sideout-navy) 0%,
    hsl(270, 30%, 18%) 50%,
    var(--sideout-navy) 100%
  );
  background-size: 200% 200%;
  animation: v2-research-gradient-drift 25s ease-in-out infinite;
}
@keyframes v2-research-gradient-drift {
  0%, 100% { background-position: 0% 0%; }
  50%      { background-position: 100% 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .v2-research__bg-primary { animation: none; }
}
/* Overlay mesh: subtle pink/blue wash on top */
.v2-research__bg-mesh {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    to top right,
    rgba(218, 24, 132, 0.10) 0%,
    transparent 50%,
    rgba(0, 156, 222, 0.10) 100%
  );
}

.v2-research__container {
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin-inline: auto;
  padding-inline: 24px;
}
@media (min-width: 768px)  { .v2-research__container { padding-inline: 48px; } }
@media (min-width: 1024px) { .v2-research__container { padding-inline: 64px; } }

.v2-research__header {
  text-align: center;
  margin-bottom: 56px;
}
.v2-research__eyebrow {
  display: inline-block;
  color: var(--sideout-pink);
  font-family: 'Red Hat Display', sans-serif;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  line-height: 24px;
}
.v2-research__title {
  margin-top: 12px;
  color: #ffffff;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 800;
  text-wrap: balance;
}
@media (min-width: 768px) {
  .v2-research__title { font-size: 48px; }
}

/* ─── Desktop row layout ────────────────────────────────── */
.v2-research__steps-row--desktop {
  display: none;
}
@media (min-width: 768px) {
  .v2-research__steps-row--desktop {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 8px;
  }
}

.v2-research__steps-col--mobile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
@media (min-width: 768px) {
  .v2-research__steps-col--mobile { display: none; }
}

/* Step card */
.v2-research__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 240px;
}
.v2-research__step--mobile {
  max-width: 260px;
}
.v2-research__step-circle {
  width: 128px;
  height: 128px;
  border-radius: 9999px;
  overflow: hidden;
  border: 3px solid #ffffff;
  margin-bottom: 16px;
  background: var(--sideout-surface);
  display: flex;
  align-items: center;
  justify-content: center;
}
.v2-research__step-circle--mobile {
  width: 112px;
  height: 112px;
  margin-bottom: 12px;
}
.v2-research__step-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.v2-research__step-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--sideout-pink) 0%, #BC0E65 100%);
  color: #ffffff;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 40px;
  font-weight: 800;
}
.v2-research__step-title {
  color: #ffffff;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  margin-bottom: 8px;
}
.v2-research__step-body {
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Red Hat Display', sans-serif;
  font-size: 16px;
  line-height: 1.625;
  margin: 0;
}

/* Arrows */
.v2-research__arrow {
  color: rgba(218, 24, 132, 0.5);
  flex-shrink: 0;
}
.v2-research__arrow--right {
  /* Positioned to align with the middle of the circle image
     (128/2 = 64; step title + body push ~62px — so ~64-8 = 56px) */
  margin-top: 56px;
  margin-inline: 8px;
}
.v2-research__arrow--right svg { width: 40px; height: 40px; }

.v2-research__arrow--down {
  margin: 8px 0;
}
.v2-research__arrow--down svg { width: 32px; height: 32px; }

/* Mobile font bump — client feedback 2026-05-29. The step descriptions
   sit at the 16px body floor AND render at 60% white on the dark gradient,
   so they read small/dim on phones. Nudge to 18px (matching the hero/about
   body) at the phone breakpoint for legibility. The eyebrow (16), step
   titles (18), and section title (30) already read fine — left as-is. */
@media (max-width: 640px) {
  .v2-research__step-body { font-size: 18px; } /* 16 → 18 */
}

@media (prefers-reduced-motion: reduce) {
  /* Content is static — no motion to clamp here. */
}
</style>
